/**
 * Override of @insite/mobius/utilities/linkElementsManipulation.
 *
 * The base implementation hardcodes `page=1` into the query string it hands back as
 * `currentQueryString`. That value is glued onto the canonical in two places -
 * client-framework/Common/Utilities/setPageMetadata (`cleanUrl(canonicalPath, currentUrl) + queryString`,
 * used by ProductListPage for both SSR and CSR) and Pagination's `updateCanonical` call - so every
 * paginated product list page self-canonicalises to `?page=1`. robots.txt disallows `/*page=`, which
 * leaves those pages pointing their canonical at a URL crawlers are told not to fetch.
 *
 * Only `getPrevCurrentNextQueryString` differs from base: a redundant `page=1` is dropped from the
 * canonical and rel="prev" query strings. Paging itself is untouched - the page number is still used
 * to derive rel="prev"/rel="next", so page 1 keeps linking to page 2, and pages 2..N keep their own
 * `?page=N` canonical.
 */
const appendLinkElement = (relValue: string, hrefValue: string) => {
    const head = document.querySelector("head");
    const link = document.createElement("link");
    link.setAttribute("rel", relValue);
    link.setAttribute("href", hrefValue);
    head?.appendChild(link);
};

const getLinkElement = (relValue: string, hrefValue: string) => {
    const linkElement = getlinkElementList().find(
        l => l.getAttribute("rel") === relValue && l.getAttribute("href") === hrefValue,
    );
    return linkElement;
};

const getlinkElementList = () => {
    return Array.from(document.getElementsByTagName("link"));
};

const removeLinkElement = (relValue: string, hrefValue: string) => {
    const head = document.querySelector("head");
    const link = getLinkElement(relValue, hrefValue);
    if (link) {
        head?.removeChild(link);
    }
};

const hasLinksWithRelPrevNext = (prev: string, next: string) => {
    const relPrevrelNext = { hasPrev: false, hasNext: false };
    const linkElementList = getlinkElementList();
    relPrevrelNext.hasPrev = linkElementList.some(
        l => l.getAttribute("rel") === "prev" && l.getAttribute("href") === prev,
    );
    relPrevrelNext.hasNext = linkElementList.some(
        l => l.getAttribute("rel") === "next" && l.getAttribute("href") === next,
    );
    return relPrevrelNext;
};

const getCurrentQueryString = (search: string) => {
    if (typeof window !== "undefined") {
        return window.location.search ?? "";
    }

    return search ?? "";
};

const getCanonical = () => {
    return window.location.origin + window.location.pathname;
};

const updateCanonical = (search: string) => {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
    }

    const currentCanonical = new URL(link.getAttribute("href") || getCanonical());
    currentCanonical.search = search;
    link.setAttribute("href", currentCanonical.toString());
};

/** Drops a redundant `page=1` so the first page of a list is not canonicalised to a paged URL. */
const removeFirstPageParameter = (queryString: string) => {
    if (!queryString) {
        return "";
    }

    const parameters = queryString
        .replace(/^\?/, "")
        .split("&")
        .filter(parameter => parameter !== "page=1");

    return parameters.length > 0 ? `?${parameters.join("&")}` : "";
};

const getPrevCurrentNextQueryString = (
    prevPageUrl: string,
    nextpageUrl: string,
    currentPage: number,
    filterQuery: string,
) => {
    let pagedQueryString = "";
    filterQuery = filterQuery ?? "";
    if (currentPage === 1) {
        if (filterQuery && filterQuery.indexOf("page=") === -1) {
            if (filterQuery.indexOf("query=") >= 0) {
                const idx = filterQuery.indexOf("&");
                if (idx >= 0) {
                    pagedQueryString = `?${filterQuery.slice(0, idx)}&page=1${filterQuery.slice(idx)}`;
                } else {
                    pagedQueryString = `?${filterQuery}&page=1`;
                }
            } else {
                pagedQueryString = `?page=1&${filterQuery}`;
            }
        } else {
            pagedQueryString = filterQuery ? `?${filterQuery}` : `?page=1`;
        }
    } else {
        const search = `?page=${currentPage}`;
        pagedQueryString = getCurrentQueryString(search);
    }

    // rel="prev"/rel="next" are still derived from the paged string, so paging is unaffected.
    const prevQueryString = prevPageUrl
        ? pagedQueryString.replace(/page=[0-9]+/, `page=${(currentPage - 1).toString()}`)
        : "";
    const nextQueryString = nextpageUrl
        ? pagedQueryString.replace(/page=[0-9]+/, `page=${(currentPage + 1).toString()}`)
        : "";

    return {
        // `next` can never be page 1, but strip uniformly rather than relying on that.
        prevQueryString: removeFirstPageParameter(prevQueryString),
        currentQueryString: removeFirstPageParameter(pagedQueryString),
        nextQueryString: removeFirstPageParameter(nextQueryString),
    };
};

export default getLinkElement;
export {
    appendLinkElement,
    getCanonical,
    getCurrentQueryString,
    getlinkElementList,
    getPrevCurrentNextQueryString,
    hasLinksWithRelPrevNext,
    removeLinkElement,
    updateCanonical,
};
